import { OrganizationRoles } from "@/shared/utils/constants";

const getRolePriority = (role?: string | null): number | null => {
  if (!role) return null;
  const item = OrganizationRoles.getItemByValue(role);
  return item && typeof item.priority === "number" ? item.priority : null;
};

/**
 * Roles a user holding `currentUserRole` is allowed to hand out. A user can
 * only assign roles at or below their own level, so a Supervisor never gets
 * the Admin option. Mirrors `get_assignable_roles` on the backend.
 */
export const getRolesByPermissionLevel = (
  currentUserRole?: string,
  rolesObject = OrganizationRoles,
) => {
  if (!currentUserRole) return [];
  const currentRole = rolesObject.getItemByValue(currentUserRole);

  if (!currentRole || typeof currentRole.priority !== "number") {
    return [];
  }

  const currentPriority = currentRole.priority;

  return rolesObject.asList().filter((roleOption: { value: string }) => {
    const role = rolesObject.getItemByValue(roleOption.value);
    return (
      role &&
      typeof role.priority === "number" &&
      role.priority <= currentPriority
    );
  });
};

/**
 * Whether a user holding `currentUserRole` may edit or delete a member or
 * invite that currently holds `targetRole`. A Supervisor can manage other
 * Supervisors and below, but never an Admin. Mirrors `can_manage_role` on the
 * backend, including treating an unknown target role as the lowest level.
 */
export const canManageRole = (
  currentUserRole?: string | null,
  targetRole?: string | null,
): boolean => {
  const currentPriority = getRolePriority(currentUserRole);
  if (currentPriority === null) return false;
  return currentPriority >= (getRolePriority(targetRole) ?? 0);
};
